import type { EditorInstance, Formats } from "@quire/editor";
import { getEditor } from "./editors/index.ts";
import { Autosaver } from "./lib/autosave.ts";
import { type DocSummary, newThoughtText, summarize } from "./lib/docs.ts";
import { download } from "./lib/download.ts";
import { type FieldValue, readField, writeField } from "./lib/fields.ts";
import { countChars, countWords, todayISO } from "./lib/format.ts";
import {
  composeDocument,
  type DocumentParts,
  type FrontMatterModel,
  parseFrontMatter,
  renderFrontMatter,
  splitDocument,
} from "./lib/front-matter.ts";
import { preparePicture } from "./lib/image-encode.ts";
import {
  describeImage,
  imageExtension,
  imageMarkdown,
  imagePath,
  imagesIn,
  MAX_IMAGE_BYTES,
  slugOfPath,
  typeFromName,
  unusedImages,
} from "./lib/images.ts";
import {
  isMarkdownName,
  isSafeName,
  renameProblem,
  slugify,
  uniqueName,
  withExtension,
} from "./lib/names.ts";
import { looksLikeNoteLink, resolveNoteLink, rewriteLinks, stemOf } from "./lib/note-links.ts";
import { type Heading, headingAt, headingByAnchor, outlineOf } from "./lib/outline.ts";
import { type Field, THOUGHTS } from "./lib/schema.ts";
import { SearchIndex } from "./lib/search.ts";
import { byteLength, ConflictError, ExistsError, PermissionError } from "./lib/store/errors.ts";
import { FsaStore } from "./lib/store/fsa.ts";
import { forgetFolder, loadFolder, saveFolder } from "./lib/store/handles.ts";
import { IdbStore } from "./lib/store/idb.ts";
import type { DocMeta, ReadResult, Store } from "./lib/store/types.ts";
import { zipDocuments } from "./lib/zip.ts";
import { createState } from "./state.ts";

const PREFIX = "quire:";
const LEGACY_PREFIX = "looseleaf:";

// Carry settings over from the working name, once.
try {
  for (let i = localStorage.length - 1; i >= 0; i -= 1) {
    const key = localStorage.key(i);
    if (key?.startsWith(LEGACY_PREFIX)) {
      const next = PREFIX + key.slice(LEGACY_PREFIX.length);
      if (localStorage.getItem(next) === null)
        localStorage.setItem(next, localStorage.getItem(key) ?? "");
      localStorage.removeItem(key);
    }
  }
} catch {}

const storage = {
  get(key: string, fallback: string | null = null): string | null {
    try {
      return localStorage.getItem(PREFIX + key) ?? fallback;
    } catch {
      return fallback;
    }
  },
  set(key: string, value: string): void {
    try {
      localStorage.setItem(PREFIX + key, value);
    } catch {}
  },
};

export interface Notice {
  text: string;
  tone: "info" | "warn";
  actions: { key: string; label: string }[];
}

export type SaveState = "idle" | "saving" | "saved" | "unsaved" | "error" | "conflict";

export interface Current {
  path: string;
  kind: "thought" | "document";
  title: string;
}

export interface DocItem extends DocSummary {
  name: string;
  updatedAt: number;
}

export type FrontMatterView =
  | { kind: "none" }
  | { kind: "broken"; errors: string[]; yaml: string }
  | { kind: "other"; yaml: string }
  | { kind: "thought"; yaml: string; fields: { field: Field; value: FieldValue }[] };

interface Open {
  path: string;
  version: string;
  parts: DocumentParts;
  model: FrontMatterModel | null;
  changed: Set<string>;
  body: string;
}

type Choice = { key: string; label: string };

export class App {
  state = createState({
    storeKind: "idb",
    storeLabel: "Browser library",
    canPickFolder: FsaStore.supported(),
    /** Whether the browser promised not to clear the library; null when it cannot say. */
    persisted: null as boolean | null,
    ready: false,
    docs: [] as DocItem[],
    query: "",
    results: null as string[] | null,
    sidebarOpen: false,
    detailsOpen: false,
    exportOpen: false,
    previewOpen: false,
    /** The command palette is open. */
    paletteOpen: false,
    /** Counts requests to rename the open file; the name above the note listens. */
    renameRev: 0,
    current: null as Current | null,
    fmRev: 0,
    save: "idle" as SaveState,
    saveDetail: "",
    autosave: storage.get("autosave", "on") !== "off",
    focus: storage.get("focus") === "on",
    typewriter: storage.get("typewriter") === "on",
    theme: (["light", "dark"].includes(storage.get("theme") ?? "") ? storage.get("theme") : null) as
      | "light"
      | "dark"
      | null,
    systemDark: globalThis.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false,
    wordCount: 0,
    charCount: 0,
    /** What the caret is in (bold, a link...), so the bar can show it. */
    caret: { active: [], link: null } as Formats,
    editorId: getEditor(storage.get("editor")).id,
    /** Lines of the open file the current editor would change if it saved. */
    notice: null as Notice | null,
    /** A picture is waiting for its description (the bar asks for it). */
    altAsk: null as { initial: string } | null,
    /** Kept pictures that no note uses. */
    unusedImages: [] as string[],
    /** The open note uses kept pictures, so it is exported as a .zip with them. */
    noteHasImages: false,
    /** The headings of the open note, and which one the reader is in. */
    headings: [] as Heading[],
    currentHeading: -1,
    /** The editor can jump to a line, so an outline is worth showing. */
    outlineReady: false,
  });

  search = new SearchIndex();

  #store: Store;
  #cache = new Map<string, { updatedAt: number; size: number; summary: DocSummary }>();
  #editor: EditorInstance | null = null;
  #beforeRaw = "";
  #outlineTimer: ReturnType<typeof setTimeout> | undefined;
  #cur: Open | null = null;
  #rev = 0;
  #savedRev = 0;
  #syncing = Promise.resolve();
  #lastSync = 0;
  #wordTimer: ReturnType<typeof setTimeout> | undefined;
  #noticeResolve: ((key: string) => void) | null = null;
  #altResolve: ((alt: string | null) => void) | null = null;
  #imageTimer: ReturnType<typeof setTimeout> | undefined;
  /** Whether any picture is kept, so saving is worth a look for unused ones. */
  #hasImages = false;

  #autosaver = new Autosaver({
    save: () => this.#write(),
    onError: () => {},
    delay: 800,
  });

  constructor({ store }: { store?: Store } = {}) {
    this.#store = store ?? new IdbStore();
    this.#applyTheme(this.state.get().theme);
    globalThis.matchMedia?.("(prefers-color-scheme: dark)").addEventListener?.("change", (e) => {
      this.state.set({ systemDark: e.matches });
    });
  }

  // ---------------------------------------------------------------- boot

  async start() {
    if (!this.#store.kind || this.#store.kind === "idb") {
      await this.#chooseStore();
    }
    this.state.set({
      storeKind: this.#store.kind,
      storeLabel: this.#store.label,
    });
    await this.sync();
    const last = storage.get(this.#lastKey());
    if (last && this.#cache.has(last)) await this.open(last, { focus: false });
    this.state.set({ ready: true });
    this.#checkPersisted();
    this.#scanImagesSoon();
  }

  /** Whether the browser keeps the library for good. */
  async #checkPersisted() {
    if (this.#store.kind !== "idb") return;
    try {
      const persisted = (await navigator.storage?.persisted?.()) ?? null;
      this.state.set({ persisted });
    } catch {}
  }

  #askedToPersist = false;

  /**
   * After the first save the browser is asked to keep the library. It is asked once the writer
   * has made something, because some browsers show a question when asked.
   */
  async #askToPersist() {
    if (this.#askedToPersist || this.#store.kind !== "idb" || !navigator.storage?.persist) return;
    this.#askedToPersist = true;
    try {
      this.state.set({ persisted: await navigator.storage.persist() });
    } catch {}
  }

  async #chooseStore() {
    if (FsaStore.supported()) {
      try {
        const handle = await loadFolder();
        if (handle) {
          const store = new FsaStore(handle);
          this.#store = store;
          if ((await store.permission()) !== "granted") this.#askForFolderAccess(store);
          return;
        }
      } catch {}
    }
    this.#store = new IdbStore();
  }

  #askForFolderAccess(store: FsaStore) {
    this.#ask(
      `Allow access to “${store.label}” to see your files.`,
      [
        { key: "allow", label: "Allow access" },
        { key: "library", label: "Use browser library" },
      ],
      "warn",
    ).then(async (key) => {
      if (key === "allow") {
        if (await store.requestPermission()) await this.sync(true);
        else this.#askForFolderAccess(store);
      } else if (key === "library") {
        await this.useBrowserLibrary();
      }
    });
  }

  #lastKey() {
    return `last:${this.#store.kind}:${this.#store.label}`;
  }

  /** The editor says what the caret is in now. */
  setCaret(caret: Formats) {
    this.state.set({ caret });
  }

  /** Called by the sheet once its editor exists. */
  attachEditor(editor: EditorInstance) {
    this.#editor = editor;
    this.state.set({
      caret: { active: [], link: null },
      outlineReady: typeof editor.goToLine === "function",
    });
    if (this.#cur) this.#load(this.#cur.body);
  }

  /** What the editor holds right now (used by tests). */
  editorValue(): string {
    return this.#editor?.getValue() ?? "";
  }

  /** Raw Markdown on and off: Plain text while it is on, the editor you came from when it is off. */
  toggleRaw() {
    const now = this.state.get().editorId;
    if (now === "plain") {
      this.setEditorId(
        this.#beforeRaw && this.#beforeRaw !== "plain" ? this.#beforeRaw : "lexical-live",
      );
    } else {
      this.#beforeRaw = now;
      this.setEditorId("plain", false);
    }
  }

  /** Switches the editor kind; the open text carries over unchanged. */
  setEditorId(id: string, remember = true) {
    const next = getEditor(id).id;
    if (remember) storage.set("editor", next);
    this.state.set({ editorId: next });
  }

  /** Lists the headings of this text. */
  #outline(body: string) {
    const headings = outlineOf(body);
    this.state.set({
      headings,
      currentHeading: headingAt(headings, this.#editor?.topLine?.() ?? 0),
    });
  }

  /** Called as the page scrolls: which heading is the reader in. */
  trackOutline() {
    const { headings } = this.state.get();
    if (headings.length === 0) return;
    this.state.set({ currentHeading: headingAt(headings, this.#editor?.topLine?.() ?? 0) });
  }

  goToHeading(index: number) {
    const heading = this.state.get().headings[index];
    if (!heading) return;
    this.#editor?.goToLine?.(heading.line);
    this.state.set({ currentHeading: index, sidebarOpen: false });
  }

  /** A link to `#some-heading` in the note: moves there. True when the heading exists. */
  goToAnchor(anchor: string): boolean {
    const index = headingByAnchor(this.state.get().headings, anchor);
    if (index < 0) return false;
    this.goToHeading(index);
    return true;
  }

  /** Puts text into the editor. */
  #load(body: string) {
    const editor = this.#editor;
    if (!editor) return;
    this.#outline(body);
    editor.setValue(body);
  }

  // ------------------------------------------------------------ documents

  /** Reads the folder or library and updates the list, reading only what changed. */
  sync(force = false) {
    const run = async () => {
      if (!force && Date.now() - this.#lastSync < 1000) return;
      this.#lastSync = Date.now();
      let metas: DocMeta[];
      try {
        metas = await this.#store.list();
      } catch (error) {
        if (error instanceof PermissionError) return;
        throw error;
      }
      const seen = new Set(metas.map((m) => m.path));
      for (const path of [...this.#cache.keys()]) {
        if (!seen.has(path)) {
          this.#cache.delete(path);
          this.search.delete(path);
        }
      }
      const stale = metas.filter((m) => {
        const c = this.#cache.get(m.path);
        return !c || c.updatedAt !== m.updatedAt || c.size !== m.size;
      });
      const reads = await Promise.all(
        stale.map(async (m) => ({ m, r: await this.#store.read(m.path) })),
      );
      for (const { m, r } of reads) {
        this.#remember(m.path, r.text, m.updatedAt, m.size);
        if (this.#cur?.path === m.path && r.version !== this.#cur.version) {
          this.#externalChange(r);
        }
      }
      this.#publishDocs();
      if (this.#cur && !seen.has(this.#cur.path)) {
        this.state.set({ save: "conflict", saveDetail: "The file was removed from the folder." });
      }
    };
    this.#syncing = this.#syncing.then(run, run);
    return this.#syncing;
  }

  #remember(path: string, text: string, updatedAt: number, size: number = byteLength(text)) {
    const summary = summarize(path, text);
    this.#cache.set(path, { updatedAt, size, summary });
    this.search.set(path, { name: path, title: summary.title, text });
  }

  #publishDocs() {
    const docs = [...this.#cache].map(([path, c]) => ({
      ...c.summary,
      path,
      name: path,
      updatedAt: c.updatedAt,
    }));
    this.state.set({ docs });
    this.setQuery(this.state.get().query);
  }

  setQuery(query: string) {
    this.state.set({
      query,
      results: query.trim() ? this.search.search(query) : null,
    });
  }

  setPalette(open: boolean) {
    this.state.set({ paletteOpen: open });
  }

  /** Asks the name above the note to open its rename box. */
  requestRename() {
    if (!this.state.get().current) return;
    this.state.set({ renameRev: this.state.get().renameRev + 1 });
  }

  /** Puts the caret back in the note, where it was. */
  focusEditor() {
    this.#editor?.focus();
  }

  toggleSidebar(open = !this.state.get().sidebarOpen) {
    this.state.set({ sidebarOpen: open });
  }

  async open(path: string, { focus = true }: { focus?: boolean } = {}) {
    if (this.#cur?.path === path) {
      if (focus) this.#editor?.focus();
      return;
    }
    if (!(await this.#leaveCurrent())) return;
    try {
      const r = await this.#store.read(path);
      this.#adopt(path, r);
    } catch (error) {
      this.#fail(error);
      return;
    }
    if (focus) this.#editor?.focus();
  }

  #adopt(path: string, r: ReadResult) {
    const parts = splitDocument(r.text);
    const model = parts.hasFrontMatter ? parseFrontMatter(parts.yaml) : null;
    this.#autosaver.cancel();
    this.#cur = { path, version: r.version, parts, model, changed: new Set(), body: parts.body };
    this.#rev = 0;
    this.#savedRev = 0;
    this.#load(parts.body);
    storage.set(this.#lastKey(), path);
    this.state.set({
      current: this.#view(),
      save: "idle",
      saveDetail: "",
      detailsOpen: false,
      wordCount: countWords(parts.body),
      charCount: countChars(parts.body),
      fmRev: this.state.get().fmRev + 1,
    });
  }

  #view(): Current | null {
    if (!this.#cur) return null;
    const summary = summarize(this.#cur.path, this.#serialize());
    return {
      path: this.#cur.path,
      kind: summary.isThought ? "thought" : "document",
      title: summary.title,
    };
  }

  /** Resolves true when it is fine to replace the open document. */
  async #leaveCurrent(): Promise<boolean> {
    if (!this.#cur || !this.isDirty()) return true;
    const name = this.#cur.path;
    if (this.state.get().autosave && this.state.get().save !== "conflict") {
      try {
        await this.#autosaver.flush();
        if (!this.isDirty()) return true;
      } catch {}
      const key = await this.#ask(
        `“${name}” could not be saved.`,
        [
          { key: "stay", label: "Stay here" },
          { key: "discard", label: "Discard changes" },
        ],
        "warn",
      );
      return key === "discard";
    }
    const key = await this.#ask(
      `Save changes to “${name}”?`,
      [
        { key: "save", label: "Save" },
        { key: "discard", label: "Don’t save" },
        { key: "stay", label: "Stay here" },
      ],
      "warn",
    );
    if (key === "save") {
      try {
        await this.#autosaver.flush();
        return !this.isDirty();
      } catch {
        return false;
      }
    }
    return key === "discard";
  }

  isDirty() {
    return this.#cur !== null && this.#rev !== this.#savedRev;
  }

  async createThought(title: string): Promise<{ ok: boolean; message?: string; path?: string }> {
    const clean = title.trim();
    if (!clean) return { ok: false, message: "Give the thought a title." };
    if (!(await this.#leaveCurrent())) return { ok: false, message: "" };
    return this.#create(
      () => uniqueName(`${slugify(clean)}.md`, this.#cache.keys()),
      newThoughtText({ title: clean, date: todayISO() }),
    );
  }

  async createDocument(name: string): Promise<{ ok: boolean; message?: string; path?: string }> {
    const file = withExtension(name);
    if (!isSafeName(file)) {
      return { ok: false, message: "Use letters, numbers, spaces, dashes and underscores." };
    }
    if ([...this.#cache.keys()].some((p) => p.toLowerCase() === file.toLowerCase())) {
      return { ok: false, message: "A file with that name already exists." };
    }
    if (!(await this.#leaveCurrent())) return { ok: false, message: "" };
    return this.#create(() => file, "");
  }

  /**
   * Gives the open file a new name. The text is not touched: the new file is written first,
   * then the old one removed, so a failure never loses the note. Links in other notes are left as they are.
   */
  async renameCurrent(name: string): Promise<{ ok: boolean; message?: string }> {
    const cur = this.#cur;
    if (!cur) return { ok: false, message: "" };
    const file = withExtension(name);
    if (file === cur.path) return { ok: true };
    const oldPath = cur.path;
    const wasThought = this.state.get().current?.kind === "thought";
    const problem = renameProblem(file, cur.path, this.#cache.keys());
    if (problem) return { ok: false, message: problem };
    // Pending edits go to the old file first, so the copy holds everything.
    if (this.isDirty()) {
      try {
        await this.#autosaver.flush();
      } catch {}
      if (this.isDirty() || this.#cur !== cur) {
        return { ok: false, message: "The note could not be saved first, so it was not renamed." };
      }
    }
    // Moves run in line with the folder checks, so a check never sees the file half moved.
    const move = async () => {
      if (file.toLowerCase() === cur.path.toLowerCase()) {
        // Only the capitals change; on a folder that ignores case, go through a temporary name.
        const temp = uniqueName(`${slugify(file)}-renaming.md`, this.#cache.keys());
        await this.#move(cur.path, temp);
        await this.#move(temp, file);
      } else {
        await this.#move(cur.path, file);
      }
    };
    const job = this.#syncing.then(move);
    this.#syncing = job.catch(() => {});
    try {
      await job;
    } catch (error) {
      if (error instanceof ExistsError) {
        await this.sync(true);
        return { ok: false, message: "A file with that name already exists." };
      }
      return { ok: false, message: (error as Error)?.message || "The file could not be renamed." };
    }
    // The question about other notes' links waits for an answer, so the rename does not.
    void this.#offerLinkUpdates(oldPath, file, wasThought);
    return { ok: true };
  }

  /**
   * After a rename: finds the notes that link to the old name (a Document by file name, a Thought
   * by its site path) and offers to point them at the new one. Notes that changed on disk since
   * they were read are skipped and named, never overwritten.
   */
  async #offerLinkUpdates(oldPath: string, newPath: string, wasThought: boolean) {
    // A quick look at the text first; only notes that mention the name are read.
    const longest = stemOf(oldPath)
      .split(/\s+/)
      .reduce((a, b) => (b.length > a.length ? b : a), "");
    const found: { path: string; version: string; text: string; count: number }[] = [];
    try {
      for (const path of this.search.mentions(longest)) {
        if (path === oldPath) continue;
        const summary = this.#cache.get(path)?.summary;
        if (!summary) continue;
        const read = await this.#store.read(path);
        const r = rewriteLinks(read.text, summary.isThought, oldPath, newPath, wasThought);
        if (r.count > 0) found.push({ path, version: read.version, text: r.text, count: r.count });
      }
    } catch (error) {
      this.#fail(error);
      return;
    }
    if (found.length === 0) return;
    const key = await this.#ask(
      `${found.length === 1 ? "1 note links" : `${found.length} notes link`} to ${oldPath}. Update ${found.length === 1 ? "it" : "them"} to ${newPath}?`,
      [
        { key: "update", label: "Update links" },
        { key: "leave", label: "Leave them" },
      ],
    );
    if (key !== "update") return;

    const updated: string[] = [];
    const skipped: string[] = [];
    for (const f of found) {
      try {
        const wrote = await this.#store.write(f.path, f.text, { expectVersion: f.version });
        this.#remember(f.path, f.text, wrote.updatedAt);
        updated.push(f.path);
      } catch (error) {
        if (error instanceof ConflictError || error instanceof PermissionError)
          skipped.push(f.path);
        else throw error;
      }
    }
    this.#publishDocs();
    // The note on screen is reloaded from the file, so what is shown matches what was saved.
    const open = this.#cur?.path;
    if (open && updated.includes(open)) {
      try {
        this.#adopt(open, await this.#store.read(open));
      } catch (error) {
        this.#fail(error);
      }
    }
    const words = (n: number) => (n === 1 ? "1 note" : `${n} notes`);
    const parts = [updated.length ? `Updated the links in ${words(updated.length)}.` : ""];
    if (skipped.length) {
      parts.push(
        `Left ${words(skipped.length)} alone because they changed meanwhile: ${skipped.join(", ")}.`,
      );
    }
    this.#say(parts.filter(Boolean).join(" "));
  }

  /** A link followed in the editor: a `#heading` of this note, or another note. True when handled. */
  followLink(address: string): boolean {
    const anchor = /^#\S+$/.test(address.trim()) ? address.trim().slice(1) : null;
    if (anchor) return this.goToAnchor(anchor);
    return this.followNoteLink(address);
  }

  /**
   * A link in the open note, followed with Ctrl or Cmd held: opens the note it names, at its
   * heading when the address has one. False when the address is not a note link at all.
   */
  followNoteLink(address: string): boolean {
    const { current, docs } = this.state.get();
    if (!current) return false;
    const fromThought = current.kind === "thought";
    const hit = resolveNoteLink(
      address,
      fromThought,
      docs.map((d) => ({ path: d.path, isThought: d.isThought })),
    );
    if (!hit) {
      if (!looksLikeNoteLink(address, fromThought)) return false;
      // A Document has no page on the site, so a Thought's link to one is left alone.
      const slug = address.trim().replace(/^\//, "").split("#")[0]!.toLowerCase();
      if (fromThought && docs.some((d) => !d.isThought && stemOf(d.path).toLowerCase() === slug)) {
        return false;
      }
      this.#say(`No note is called ${address}.`);
      return true;
    }
    void this.#openLinked(hit.path, hit.anchor);
    return true;
  }

  async #openLinked(path: string, anchor: string) {
    await this.open(path);
    if (anchor && this.#cur?.path === path) this.goToAnchor(anchor);
  }

  /** Copies a file to a new name, removes the old one, and follows the move in memory. */
  async #move(from: string, to: string) {
    const read = await this.#store.read(from);
    const wrote = await this.#store.write(to, read.text, { create: true });
    await this.#store.remove(from);
    this.#cache.delete(from);
    this.search.delete(from);
    this.#remember(to, read.text, wrote.updatedAt);
    const cur = this.#cur;
    if (cur && cur.path === from) {
      cur.path = to;
      cur.version = wrote.version;
      storage.set(this.#lastKey(), to);
    }
    this.#publishDocs();
    if (this.#cur?.path === to) this.state.set({ current: this.#view() });
  }

  async #create(
    pickName: () => string,
    text: string,
  ): Promise<{ ok: boolean; message?: string; path?: string }> {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const path = pickName();
      try {
        const res = await this.#store.write(path, text, { create: true });
        this.#remember(path, text, res.updatedAt);
        this.#publishDocs();
        const read = await this.#store.read(path);
        this.#adopt(path, read);
        this.#editor?.focus();
        return { ok: true, path };
      } catch (error) {
        if (error instanceof ExistsError) {
          await this.sync(true);
          continue;
        }
        this.#fail(error);
        return { ok: false, message: "That file could not be created." };
      }
    }
    return { ok: false, message: "That file could not be created." };
  }

  async importFiles(files: File[] | FileList) {
    let imported = 0;
    let skipped = 0;
    for (const file of [...files]) {
      if (!isMarkdownName(file.name)) {
        skipped += 1;
        continue;
      }
      const wanted = isSafeName(file.name)
        ? file.name
        : `${slugify(file.name.replace(/\.\w+$/, ""))}.md`;
      const path = uniqueName(wanted, this.#cache.keys());
      try {
        const text = await file.text();
        const res = await this.#store.write(path, text, { create: true });
        this.#remember(path, text, res.updatedAt);
        imported += 1;
      } catch (error) {
        this.#fail(error);
        return;
      }
    }
    this.#publishDocs();
    const parts: string[] = [];
    if (imported) parts.push(`Imported ${imported} ${imported === 1 ? "file" : "files"}.`);
    if (skipped)
      parts.push(`Skipped ${skipped} that ${skipped === 1 ? "is" : "are"} not Markdown.`);
    if (parts.length) this.#say(parts.join(" "));
  }

  async pickFolder() {
    let store: FsaStore;
    try {
      store = await FsaStore.pick();
    } catch (error) {
      if ((error as Error)?.name === "AbortError") return;
      this.#fail(error);
      return;
    }
    if (!(await this.#leaveCurrent())) return;
    await saveFolder(store.dir);
    await this.#switchStore(store);
  }

  async useBrowserLibrary() {
    if (!(await this.#leaveCurrent())) return;
    await forgetFolder().catch(() => {});
    await this.#switchStore(new IdbStore());
  }

  async #switchStore(store: Store) {
    this.#autosaver.cancel();
    this.#store = store;
    this.#cur = null;
    this.#rev = 0;
    this.#savedRev = 0;
    this.#cache.clear();
    this.search.clear();
    this.#editor?.setValue("");
    this.state.set({
      headings: [],
      currentHeading: -1,
      storeKind: store.kind,
      storeLabel: store.label,
      current: null,
      docs: [],
      query: "",
      results: null,
      save: "idle",
      notice: null,
      detailsOpen: false,
      wordCount: 0,
      charCount: 0,
    });
    await this.sync(true);
    const last = storage.get(this.#lastKey());
    if (last && this.#cache.has(last)) await this.open(last, { focus: false });
  }

  // -------------------------------------------------------------- editing

  onEditorChange(value: string) {
    if (!this.#cur) return;
    this.#cur.body = value;
    this.#touch();
    clearTimeout(this.#wordTimer);
    this.#wordTimer = setTimeout(
      () => this.state.set({ wordCount: countWords(value), charCount: countChars(value) }),
      250,
    );
    clearTimeout(this.#outlineTimer);
    this.#outlineTimer = setTimeout(() => this.#outline(value), 300);
  }

  #touch() {
    this.#rev += 1;
    this.state.set({ save: this.state.get().save === "conflict" ? "conflict" : "unsaved" });
    if (this.state.get().autosave && this.state.get().save !== "conflict") {
      this.#autosaver.schedule();
    }
  }

  #serialize(): string {
    return composeDocument(this.#cur!.parts, this.#cur!.body);
  }

  // ---------------------------------------------------------- front matter

  /** What the Details panel shows for the open document. */
  getFrontMatterView(): FrontMatterView | null {
    const cur = this.#cur;
    if (!cur) return null;
    if (!cur.parts.hasFrontMatter) return { kind: "none" };
    const { parts } = cur;
    const model = cur.model!;
    if (model.errors.length > 0) return { kind: "broken", errors: model.errors, yaml: parts.yaml };
    const isThought = THOUGHTS.required.every((key) => model.doc.has(key));
    if (!isThought) return { kind: "other", yaml: parts.yaml };
    return {
      kind: "thought",
      yaml: parts.yaml,
      fields: THOUGHTS.fields.map((field) => ({ field, value: readField(model.doc, field) })),
    };
  }

  setField(key: string, value: string | string[] | boolean) {
    const cur = this.#cur;
    const field = THOUGHTS.fields.find((f) => f.key === key);
    if (!cur?.model || !field) return;
    writeField(cur.model.doc, field, value);
    cur.changed.add(key);
    cur.parts.yaml = renderFrontMatter(cur.model, cur.changed);
    this.#afterFrontMatterEdit();
  }

  /** Replaces the whole front matter from text. Nothing changes if it is not valid YAML. */
  setRawFrontMatter(yaml: string): { ok: boolean; errors: string[] } {
    const cur = this.#cur;
    if (!cur) return { ok: false, errors: [] };
    const model = parseFrontMatter(yaml);
    if (model.errors.length > 0) return { ok: false, errors: model.errors };
    cur.model = model;
    cur.changed = new Set();
    cur.parts.hasFrontMatter = true;
    cur.parts.yaml = yaml;
    this.#afterFrontMatterEdit();
    return { ok: true, errors: [] };
  }

  /** Gives a document with no front matter the Thoughts keys. */
  addFrontMatter() {
    const cur = this.#cur;
    if (!cur || cur.parts.hasFrontMatter) return;
    const fresh = splitDocument(newThoughtText({ title: "", date: todayISO() }));
    cur.parts.hasFrontMatter = true;
    cur.parts.yaml = fresh.yaml;
    cur.parts.closeDelim = "---";
    cur.parts.gap = Math.max(1, fresh.gap);
    cur.model = parseFrontMatter(fresh.yaml);
    cur.changed = new Set();
    this.#afterFrontMatterEdit();
  }

  #afterFrontMatterEdit() {
    const cur = this.#cur!;
    const text = this.#serialize();
    const summary = summarize(cur.path, text);
    const cached = this.#cache.get(cur.path);
    if (cached) {
      this.#cache.set(cur.path, { ...cached, summary });
      this.search.set(cur.path, { name: cur.path, title: summary.title, text });
      this.#publishDocs();
    }
    this.#touch();
    this.state.set({ current: this.#view(), fmRev: this.state.get().fmRev + 1 });
  }

  // --------------------------------------------------------------- saving

  /** Saves now. Rejects only through the notices; callers just call it. */
  async save() {
    if (!this.#cur) return;
    try {
      await this.#autosaver.flush();
    } catch {}
  }

  async #write() {
    const cur = this.#cur;
    if (!cur || this.#rev === this.#savedRev) return;
    const rev = this.#rev;
    const text = this.#serialize();
    this.state.set({ save: "saving", saveDetail: "" });
    try {
      const res = await this.#store.write(cur.path, text, { expectVersion: cur.version });
      if (this.#cur !== cur) return;
      cur.version = res.version;
      this.#savedRev = rev;
      this.#remember(cur.path, text, res.updatedAt);
      this.#publishDocs();
      if (this.#hasImages) this.#scanImagesSoon();
      this.#askToPersist();
      const clean = this.#rev === this.#savedRev;
      this.state.set({ save: clean ? "saved" : "unsaved", saveDetail: "" });
      if (!clean && this.state.get().autosave) this.#autosaver.schedule();
    } catch (error) {
      if (this.#cur === cur) this.#saveFailed(error);
      throw error;
    }
  }

  #saveFailed(error: unknown) {
    if (error instanceof ConflictError) {
      this.state.set({ save: "conflict", saveDetail: "The file changed outside Quire." });
      this.#resolveConflict();
    } else if (error instanceof PermissionError) {
      this.state.set({ save: "error", saveDetail: "Access to the folder was lost." });
      if (this.#store instanceof FsaStore) this.#askForFolderAccess(this.#store);
    } else {
      this.state.set({
        save: "error",
        saveDetail: (error as Error)?.message ?? "The file could not be saved.",
      });
    }
  }

  #externalChange(read: ReadResult) {
    if (!this.isDirty()) {
      this.#adopt(this.#cur!.path, read);
      return;
    }
    this.state.set({ save: "conflict", saveDetail: "The file changed outside Quire." });
    this.#resolveConflict();
  }

  #resolveConflict() {
    const cur = this.#cur;
    if (!cur) return;
    this.#autosaver.cancel();
    this.#ask(
      `“${cur.path}” changed outside Quire, and you have unsaved changes.`,
      [
        { key: "reload", label: "Reload from disk" },
        { key: "keep", label: "Keep mine" },
        { key: "copy", label: "Save mine as a copy" },
      ],
      "warn",
    ).then(async (key) => {
      if (this.#cur !== cur) return;
      try {
        if (key === "reload") {
          this.#adopt(cur.path, await this.#store.read(cur.path));
        } else if (key === "keep") {
          cur.version = (await this.#store.read(cur.path)).version;
          this.state.set({ save: "unsaved" });
          await this.save();
        } else if (key === "copy") {
          const text = this.#serialize();
          const stem = cur.path.replace(/\.\w+$/, "");
          const path = uniqueName(`${stem}-copy.md`, this.#cache.keys());
          const res = await this.#store.write(path, text, { create: true });
          this.#remember(path, text, res.updatedAt);
          this.#publishDocs();
          this.#adopt(cur.path, await this.#store.read(cur.path));
          this.#say(`Saved your version as “${path}”.`);
        }
      } catch (error) {
        this.#fail(error);
      }
    });
  }

  /** Best-effort save when the page is hidden or closing. */
  flushQuietly() {
    if (this.isDirty() && this.state.get().autosave && this.state.get().save !== "conflict") {
      this.#autosaver.flush().catch(() => {});
    }
  }

  setAutosave(on: boolean) {
    storage.set("autosave", on ? "on" : "off");
    this.state.set({ autosave: on });
    if (on && this.isDirty() && this.state.get().save !== "conflict") this.#autosaver.schedule();
    if (!on) this.#autosaver.cancel();
  }

  /** Focus mode fades all but the paragraph being written. It is a view, never part of the file. */
  setFocus(on: boolean) {
    storage.set("focus", on ? "on" : "off");
    this.state.set({ focus: on });
  }

  setTypewriter(on: boolean) {
    storage.set("typewriter", on ? "on" : "off");
    this.state.set({ typewriter: on });
  }

  // ---------------------------------------------------------------- theme

  effectiveTheme() {
    const { theme, systemDark } = this.state.get();
    return theme ?? (systemDark ? "dark" : "light");
  }

  /** The first use leaves "follow the system"; after that the choice is remembered. */
  toggleTheme() {
    const next = this.effectiveTheme() === "dark" ? "light" : "dark";
    storage.set("theme", next);
    this.#applyTheme(next);
    this.state.set({ theme: next });
  }

  #applyTheme(theme: string | null) {
    if (theme) document.documentElement.dataset.theme = theme;
    else delete document.documentElement.dataset.theme;
  }

  // --------------------------------------------------------------- panels

  setDetails(open: boolean) {
    this.state.set({
      detailsOpen: open,
      exportOpen: false,
      previewOpen: false,
    });
  }

  setPreview(open: boolean) {
    this.state.set({
      previewOpen: open,
      detailsOpen: false,
      exportOpen: false,
    });
  }

  /** The open file as it would be saved right now. */
  currentText(): string {
    return this.#cur ? this.#serialize() : "";
  }

  setExport(open: boolean) {
    // Without a linked folder the pictures live only in this browser, so a note that uses
    // them is exported as a .zip together with them.
    const noteHasImages =
      open &&
      this.#store.kind !== "fsa" &&
      this.#cur !== null &&
      imagesIn(this.#serialize()).size > 0;
    this.state.set({
      exportOpen: open,
      detailsOpen: false,
      previewOpen: false,
      noteHasImages,
    });
  }

  // --------------------------------------------------------------- export

  async exportCurrent() {
    if (!this.#cur) return;
    const { path } = this.#cur;
    const text = this.#serialize();
    this.state.set({ exportOpen: false });
    const used = this.#store.kind === "fsa" ? new Set<string>() : imagesIn(text);
    if (used.size === 0) return download(path, text);
    try {
      const images = [];
      for (const name of used) {
        const blob = await this.#store.readAsset(name);
        if (blob) images.push({ name, data: new Uint8Array(await blob.arrayBuffer()) });
      }
      download(
        `${slugOfPath(path)}.zip`,
        (await zipDocuments([{ name: path, text }], images)) as BlobPart,
        "application/zip",
      );
    } catch (error) {
      this.#fail(error);
    }
  }

  async exportAll() {
    this.state.set({ exportOpen: false });
    try {
      const all = await this.#store.readAll();
      const images = await Promise.all(
        (await this.#store.listAssets()).map(async (name) => ({
          name,
          data: new Uint8Array((await (await this.#store.readAsset(name))?.arrayBuffer()) ?? []),
        })),
      );
      const files = all.map((d) => ({
        name: d.path,
        text: this.#cur?.path === d.path && this.isDirty() ? this.#serialize() : d.text,
      }));
      if (files.length === 0) {
        this.#say("There is nothing to export yet.");
        return;
      }
      download(
        `quire-${todayISO()}.zip`,
        (await zipDocuments(files, images)) as BlobPart,
        "application/zip",
      );
    } catch (error) {
      this.#fail(error);
    }
  }

  // --------------------------------------------------------------- images

  /**
   * Keeps a picture in an images folder beside the documents and returns the
   * Markdown that shows it, or null when it could not be kept.
   */
  async addImage(file: File): Promise<string | null> {
    if (file.size > MAX_IMAGE_BYTES) {
      this.#say("That image is over 25 MB. Please choose a smaller one.");
      return null;
    }
    const mime = file.type || typeFromName(file.name);
    if (!imageExtension(mime)) {
      this.#say("Quire keeps PNG, JPEG, GIF, WebP, AVIF and SVG images.");
      return null;
    }
    // The description comes first; cancelling keeps nothing.
    const fallback = describeImage(file.name);
    const asked = await this.askAlt(fallback);
    if (asked === null) return null;
    const alt =
      asked
        .replace(/[[\]\r\n]+/g, " ")
        .replace(/\s+/g, " ")
        .trim() || fallback;
    try {
      const prepared = await preparePicture(file, mime);
      if (!prepared) {
        this.#say("That image could not be read.");
        return null;
      }
      const path = imagePath(
        slugOfPath(this.#cur?.path ?? "image"),
        file.name,
        prepared.hash,
        prepared.ext,
      );
      // The same picture added twice is kept once.
      if (!(await this.#store.listAssets()).includes(path)) {
        await this.#store.writeAsset(path, prepared.blob);
      }
      this.#hasImages = true;
      this.#scanImagesSoon();
      return imageMarkdown(alt, path);
    } catch (error) {
      this.#fail(error);
      return null;
    }
  }

  /** Asks the bar for a picture's description; null when cancelled. */
  askAlt(initial: string): Promise<string | null> {
    this.#altResolve?.(null);
    return new Promise((resolve) => {
      this.#altResolve = resolve;
      this.state.set({ altAsk: { initial } });
    });
  }

  answerAlt(value: string | null) {
    const resolve = this.#altResolve;
    this.#altResolve = null;
    this.state.set({ altAsk: null });
    resolve?.(value);
  }

  // ------------------------------------------------------- unused pictures

  #scanImagesSoon() {
    clearTimeout(this.#imageTimer);
    this.#imageTimer = setTimeout(() => void this.scanImages(), 2000);
  }

  /** Looks for kept pictures that no note uses. */
  async scanImages(): Promise<string[]> {
    try {
      const kept = await this.#store.listAssets();
      this.#hasImages = kept.length > 0;
      if (kept.length === 0) {
        this.state.set({ unusedImages: [] });
        return [];
      }
      const texts = (await this.#store.readAll()).map((d) =>
        this.#cur?.path === d.path ? this.#serialize() : d.text,
      );
      const unused = unusedImages(kept, texts);
      this.state.set({ unusedImages: unused });
      return unused;
    } catch {
      return this.state.get().unusedImages;
    }
  }

  /** Lists the unused pictures and, if the writer agrees, deletes them. */
  async reviewUnusedImages() {
    const unused = await this.scanImages();
    if (unused.length === 0) {
      this.#say("Every picture is used by a note.");
      return;
    }
    const names = unused.slice(0, 6).map((p) => p.replace(/^images\//, ""));
    const more = unused.length > names.length ? ` and ${unused.length - names.length} more` : "";
    const key = await this.#ask(
      `${unused.length === 1 ? "This picture is" : `These ${unused.length} pictures are`} not used by any note: ${names.join(", ")}${more}. Delete ${unused.length === 1 ? "it" : "them"}?`,
      [
        { key: "delete", label: "Delete" },
        { key: "keep", label: "Keep" },
      ],
      "warn",
    );
    if (key !== "delete") return;
    try {
      // Look again right before deleting: a note may have started using one meanwhile.
      for (const path of await this.scanImages()) await this.#store.removeAsset(path);
      await this.scanImages();
    } catch (error) {
      this.#fail(error);
    }
  }

  /** A picture kept by Quire, for the preview. */
  readImage(path: string): Promise<Blob | null> {
    return this.#store.readAsset(path).catch(() => null);
  }

  // -------------------------------------------------------------- notices

  /** Shows a notice with buttons and resolves with the key of the one pressed. */
  #ask(text: string, actions: Choice[], tone: "info" | "warn" = "info"): Promise<string> {
    this.#noticeResolve?.("dismiss");
    return new Promise<string>((resolve) => {
      this.#noticeResolve = resolve;
      this.state.set({ notice: { text, tone, actions } });
    });
  }

  #say(text: string) {
    this.#ask(text, [{ key: "close", label: "Close" }], "info");
  }

  answerNotice(key: string) {
    const resolve = this.#noticeResolve;
    this.#noticeResolve = null;
    this.state.set({ notice: null });
    resolve?.(key);
  }

  #fail(error: unknown) {
    if (error instanceof PermissionError && this.#store instanceof FsaStore) {
      this.#askForFolderAccess(this.#store);
      return;
    }
    this.#say((error as Error)?.message ?? "Something went wrong.");
  }
}
